import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import Topbar from "@/components/layout/Topbar";
import { addDays, toYMD } from "@/lib/format";
import { useProperties } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { RefreshCw, Printer, Plus } from "lucide-react";

import KpiCards from "@/components/dashboard/KpiCards";
import ReservationsPanel from "@/components/dashboard/ReservationsPanel";
import TodayActivityPanel from "@/components/dashboard/TodayActivityPanel";
import AttentionInbox from "@/components/dashboard/AttentionInbox";
import Forecast7Days from "@/components/dashboard/Forecast7Days";
import PerformancePanel from "@/components/dashboard/PerformancePanel";
import HousekeepingPanel from "@/components/dashboard/HousekeepingPanel";
import OutstandingPanel from "@/components/dashboard/OutstandingPanel";
import DistributionPanel from "@/components/dashboard/DistributionPanel";
import OnboardingBanner from "@/components/dashboard/OnboardingBanner";

const RATE_MANAGE_ROLES = new Set([
    "super_admin", "hotel_admin", "property_manager", "revenue_manager", "finance_staff",
]);

export default function Dashboard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { activePropertyId, properties } = useProperties();
    const { user } = useAuth();
    const canSeeRevenue = RATE_MANAGE_ROLES.has(user?.role);

    const [overview, setOverview] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [revenue, setRevenue] = useState([]);
    const [revenuePrev, setRevenuePrev] = useState([]);
    const [channels, setChannels] = useState([]);
    const [forecast, setForecast] = useState(null);
    const [activity, setActivity] = useState(null);
    const [perfRange, setPerfRange] = useState(30);
    const [perfMetric, setPerfMetric] = useState("revenue");
    const [reloadKey, setReloadKey] = useState(0);

    const today = toYMD(new Date());
    const propQ    = activePropertyId && activePropertyId !== "all" ? `?property_id=${activePropertyId}` : "";
    const propQAmp = activePropertyId && activePropertyId !== "all" ? `&property_id=${activePropertyId}` : "";

    // ── Data fetching ─────────────────────────────────────────
    useEffect(() => {
        // Reservations: fetch a wide window (today - 14 days → today + 30 days)
        // so we can serve arrivals/departures/in-house/balance from the same
        // memory set (no per-tab round-trip).
        const rangeFrom = toYMD(addDays(new Date(today), -14));
        const rangeTo   = toYMD(addDays(new Date(today), 30));
        const resvUrl = `/reservations?from_date=${rangeFrom}&to_date=${rangeTo}${propQAmp}`;

        Promise.all([
            api.get(`/reports/overview${propQ}`),
            api.get(resvUrl),
            api.get(`/rooms${propQ}`).catch(() => ({ data: [] })),
            api.get(`/reports/revenue-series?days=${perfRange}${propQAmp}`),
            api.get(`/reports/revenue-series?days=${perfRange}&offset_days=${perfRange}${propQAmp}`),
            api.get(`/reports/dashboard-activity${propQ}`),
            api.get("/channels").catch(() => ({ data: [] })),
        ]).then(([o, r, rms, rev, revPrev, act, c]) => {
            setOverview(o.data);
            setReservations(r.data || []);
            setRooms(rms.data || []);
            setRevenue(rev.data || []);
            setRevenuePrev(revPrev.data || []);
            setActivity(act.data);
            setChannels(c.data || []);
        }).catch(() => {});
    }, [activePropertyId, perfRange, reloadKey]);

    // 7-day forecast — only when a specific property is selected
    useEffect(() => {
        if (!activePropertyId || activePropertyId === "all") { setForecast(null); return; }
        const from = today;
        const to = toYMD(addDays(new Date(today), 7));
        api.get(`/reservations/calendar-summary?property_id=${activePropertyId}&from=${from}&to=${to}`)
            .then((r) => setForecast(r.data))
            .catch(() => setForecast(null));
    }, [activePropertyId, reloadKey]);

    // ── Derived data (uses the SAME rule as backend overview counters) ─────
    const active = (r) => !["cancelled", "no_show"].includes(r.status);
    const arrivals   = reservations.filter((r) => r.check_in  === today && active(r));
    const dirtyRoomIds       = new Set(rooms.filter((r) => r.status === "dirty").map((r) => r.id));
    const dirtyArrivalCount  = arrivals.filter((a) => a.room_id && dirtyRoomIds.has(a.room_id)).length;
    const unassignedArrivals = arrivals.filter((a) => !a.room_id).length;
    const badChannels        = channels.filter((c) => !["connected", "active", "healthy"].includes(c.status)).length;
    const currency = properties.find((p) => p.id === activePropertyId)?.currency
        || properties[0]?.currency
        || "EUR";

    // 7-day per-day roll-up
    const forecastDays = useMemo(() => {
        if (!forecast?.rows) return [];
        const byDate = {};
        for (const row of forecast.rows) {
            if (!byDate[row.date]) byDate[row.date] = {
                date: row.date, total: 0, available: 0, reserved: 0, blocked: 0, rate_sum: 0, rate_ct: 0,
            };
            const b = byDate[row.date];
            b.total     += row.total_inventory || 0;
            b.available += Math.max(0, row.available_inventory || 0);
            b.reserved  += row.reserved || 0;
            b.blocked   += row.blocked || 0;
            if (row.effective_rate) { b.rate_sum += row.effective_rate; b.rate_ct += 1; }
        }
        const arrCount = {}, depCount = {};
        for (const r of reservations) {
            if (r.check_in  && active(r)) arrCount[r.check_in]  = (arrCount[r.check_in]  || 0) + 1;
            if (r.check_out && active(r)) depCount[r.check_out] = (depCount[r.check_out] || 0) + 1;
        }
        return Object.values(byDate).map((d) => ({
            ...d,
            occ_pct:    d.total ? Math.round(((d.total - d.available) / d.total) * 100) : 0,
            arrivals:   arrCount[d.date] || 0,
            departures: depCount[d.date] || 0,
            adr:        d.rate_ct ? Math.round(d.rate_sum / d.rate_ct) : null,
        })).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 7);
    }, [forecast, reservations]);

    // Outstanding payment buckets
    const paymentBuckets = useMemo(() => {
        const t0 = today;
        let overdue = 0, dueToday = 0, upcoming = 0;
        for (const r of reservations) {
            if (!["pending", "partial"].includes(r.payment_status || "")) continue;
            if (["cancelled", "no_show"].includes(r.status)) continue;
            const bal = (r.total_amount || 0) - (r.paid_amount || 0);
            if (bal <= 0.01) continue;
            const dueDate = r.check_in;
            if (dueDate < t0) overdue += bal;
            else if (dueDate === t0) dueToday += bal;
            else upcoming += bal;
        }
        return { overdue, dueToday, upcoming };
    }, [reservations]);

    // ── Row action → quick check-in / check-out / cancel from the panel ───
    const handleQuickAction = useCallback(async (kind, res) => {
        try {
            const payload = kind === "check_in"  ? { status: "checked_in" }
                          : kind === "check_out" ? { status: "checked_out" }
                          :                        { status: "cancelled" };
            await api.patch(`/reservations/${res.id}`, payload);
            toast.success(t(`dashboard.action_${kind === "cancel" ? "cancel" : kind}`));
            setReloadKey((k) => k + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    }, [t]);

    const activeProperty = properties.find((p) => p.id === activePropertyId);

    // ── Renders ────────────────────────────────────────────────
    return (
        <>
            <Topbar title={t("dashboard.title")} subtitle={activeProperty?.name || ""} />
            <div
                className="flex-1 overflow-x-hidden overflow-y-auto bg-background px-4 pb-10 pt-5 sm:px-6"
                data-testid="dashboard-root"
            >
                <OnboardingBanner />
                {/* ── Page header + actions ────────────────────── */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-[22px] font-semibold tracking-tight text-foreground sm:text-[24px]">
                        {t("dashboard.title")}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setReloadKey((k) => k + 1)}
                            data-testid="btn-refresh"
                            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[12.5px] font-medium text-foreground/90 transition hover:border-border/80 hover:text-foreground"
                        >
                            <RefreshCw size={13} /> {t("dashboard.refresh")}
                        </button>
                        <button
                            onClick={() => window.print()}
                            data-testid="btn-print"
                            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[12.5px] font-medium text-foreground/90 transition hover:border-border/80 hover:text-foreground"
                        >
                            <Printer size={13} /> {t("dashboard.print")}
                        </button>
                        <button
                            onClick={() => navigate("/reservations?new=1")}
                            data-testid="btn-new-reservation"
                            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#ff7b02] px-3.5 text-[12.5px] font-semibold text-white shadow-sm transition hover:bg-[#e56d00]"
                        >
                            <Plus size={13} /> {t("dashboard.new_reservation")}
                        </button>
                    </div>
                </div>

                {/* ── KPI cards ─────────────────────────────────── */}
                <KpiCards overview={overview} currency={currency} canSeeRevenue={canSeeRevenue} />

                {/* ── Primary workspace — Reservations + Today's Activity ── */}
                <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,58fr)_minmax(0,42fr)]">
                    <ReservationsPanel
                        reservations={reservations}
                        rooms={rooms}
                        currency={currency}
                        onQuickAction={handleQuickAction}
                    />
                    <TodayActivityPanel activity={activity} currency={currency} />
                </div>

                {/* ── Attention inbox — compact, below workspace ── */}
                <div className="mt-4">
                    <AttentionInbox
                        overview={overview}
                        dirtyArrivalCount={dirtyArrivalCount}
                        unassignedArrivals={unassignedArrivals}
                        badChannels={badChannels}
                    />
                </div>

                {/* ── 7-day forecast strip ────────────────────────── */}
                {forecastDays.length > 0 && (
                    <div className="mt-4">
                        <Forecast7Days days={forecastDays} currency={currency} />
                    </div>
                )}

                {/* ── Performance + Housekeeping ────────────────── */}
                <div className="mt-4 grid gap-4 lg:grid-cols-[7fr_3fr]">
                    {canSeeRevenue ? (
                        <PerformancePanel
                            data={revenue}
                            prevData={revenuePrev}
                            metric={perfMetric}
                            setMetric={setPerfMetric}
                            range={perfRange}
                            setRange={setPerfRange}
                            currency={currency}
                        />
                    ) : <div />}
                    <HousekeepingPanel overview={overview} />
                </div>

                {/* ── Bottom row — Payments + Distribution ─────── */}
                {canSeeRevenue && (
                    <div className="mt-4 grid gap-4 lg:grid-cols-2">
                        <OutstandingPanel
                            overview={overview}
                            currency={currency}
                            buckets={paymentBuckets}
                        />
                        <DistributionPanel
                            channels={channels}
                            reservations={reservations}
                        />
                    </div>
                )}
            </div>
        </>
    );
}
